<template>
  <div class="app" :class="{'mosaic-page': currentRoute == '/chat'}">
    <div v-if="billingRestrictionMessage" class="billing-restriction-modal" role="dialog" aria-modal="true" aria-labelledby="billingRestrictionTitle">
      <div class="billing-restriction-modal__backdrop" @click="dismissBillingRestrictionMessage"></div>
      <div class="billing-restriction-modal__content">
        <button
          type="button"
          class="billing-restriction-modal__close"
          aria-label="Fechar"
          @click="dismissBillingRestrictionMessage"
        >
          <i class="flaticon-close"></i>
        </button>

        <div class="billing-restriction-modal__icon">
          <i class="flaticon-warning"></i>
        </div>

        <h5 id="billingRestrictionTitle" class="billing-restriction-modal__title">
          Cadastro inativo
        </h5>

        <p class="billing-restriction-modal__message">
          {{ billingRestrictionMessage }}
        </p>

        <button
          type="button"
          class="default-btn position-relative transition border-0 fw-medium text-white pt-11 pb-11 ps-25 pe-25 rounded-1 bg-success fs-md-15 fs-lg-16 w-100"
          @click="dismissBillingRestrictionMessage"
        >
          Entendi
        </button>
      </div>
    </div>

    <div v-if="!loading">
      <MainHeader
        v-if="isLogged && currentRoute !== '/' && currentRoute !== '/chat'"
        @request-logout="logoutModalOpen = true"
      />
      <MainSidebar
        v-if="isLogged && currentRoute !== '/' && !mobileChatOpen"
        @request-logout="logoutModalOpen = true"
      />
      <LogoutModal
        v-if="isLogged && currentRoute !== '/'"
        :open="logoutModalOpen"
        @close="logoutModalOpen = false"
      />
      <div class="main-content d-flex flex-column transition overflow-hidden custom-chat-page-style" :class="{'center-main-content': !isLogged, 'justify-content-start validate-home-page': currentRoute === '/', 'attendants-day-main': currentRoute === '/attendants-day', 'chat-main': currentRoute === '/chat', 'mobile-chat-open': mobileChatOpen, 'with-mobile-bottom-nav': isLogged && currentRoute !== '/'}">
        <CoupleToolbar v-if="isLogged" />
        <router-view :key="`${currentRoute}:${getAccountViewRevision}`" />
        <MainFooter v-if="isLogged && currentRoute !== '/' && currentRoute !== '/chat'" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, watchEffect } from "vue";
import { mapActions, mapGetters } from 'vuex';
import { useRoute } from 'vue-router';
import stateStore from "./utils/store";

import { isAuthenticated } from './services/authService';
import { initializeLorenaVoice, stopLorenaVoice } from './services/lorenaVoiceService';
import { ACCESS_PAGES, canAccessPage, PUBLIC_PATHS } from './utils/accessControl';

import MainHeader from "./components/Layouts/MainHeader.vue";
import MainSidebar from "./components/Layouts/MainSidebar.vue";
import MainFooter from "./components/Layouts/MainFooter.vue";
import CoupleToolbar from "./components/Financial/CoupleToolbar.vue";
import LogoutModal from "./components/Authentication/LogoutModal.vue";

export default defineComponent({
  name: "App",
  data () {
    return {
      coupleTimer: 0,
      isLogged: false,
      loading: true,
      mobileChatOpen: false,
      logoutModalOpen: false,
      billingRestrictionMessage: sessionStorage.getItem("billingRestrictionMessage") || ""
    }
  },
  components: {
    MainHeader,
    MainSidebar,
    MainFooter,
    LogoutModal,
    CoupleToolbar,
  },
  methods: {
    ...mapActions([
      "setAuthenticatedUser",
      "setAccessControl",
      "setConfiguration",
      "startFinanceRealtime",
      "stopFinanceRealtime", "fetchCouple", "resetCoupleViews"
    ]),
    async refreshCouple() {
      if (!this.isLogged) return;
      await this.fetchCouple().catch(() => undefined);
    },
    async revokeCoupleView() {
      await this.resetCoupleViews();
      await this.refreshCouple();
    },
    onMobileChatOpenChange(event: Event) {
      const customEvent = event as CustomEvent;
      this.mobileChatOpen = Boolean(customEvent.detail?.open);
    },
    updateBillingRestrictionMessage(event: Event) {
      const customEvent = event as CustomEvent;
      this.billingRestrictionMessage = customEvent.detail || "";
    },
    dismissBillingRestrictionMessage() {
      this.billingRestrictionMessage = "";
      sessionStorage.removeItem("billingRestrictionMessage");
    }
  },
  computed: {
    ...mapGetters([
      'getPermissions', 'getAccountViewRevision',
      'getAllowedPages'
    ]),
  },
  watch:{
    currentRoute(newRoute, oldRoute){
      if(!this.loading && this.isLogged && newRoute !== oldRoute){
        this.setConfiguration();
      }
    },
    getAllowedPages(data){
      const routeAccessKey = (this.$route.meta && this.$route.meta.accessKey) as string | undefined;
      if(routeAccessKey && !canAccessPage(data, routeAccessKey)){
        const page = ACCESS_PAGES.find((item) => item.key === routeAccessKey);
        this.$router.replace({
          path: "/access-denied",
          query: {
            module: page?.label || "este modulo",
            key: routeAccessKey,
          },
        });
      }
    }
  },
  async created() {
    const friendcode = new URLSearchParams(window.location.search).get('friendcode');
    if (friendcode) sessionStorage.setItem('couple.friendcode', friendcode);
    if(await isAuthenticated()){
      await this.setAuthenticatedUser();
      await this.setAccessControl();
      await this.fetchCouple().catch(() => undefined);
      await this.startFinanceRealtime();
      await initializeLorenaVoice().catch(() => undefined);

      if(PUBLIC_PATHS.includes(window.location.pathname)){
        window.location.href = sessionStorage.getItem("couple.friendcode") ? "/configurations" : "/dashboard";
        return;
      }
      this.isLogged = true;
      this.loading = false;
    } else {
      this.isLogged = false;
      if(window.location.pathname !== "/" && window.location.pathname !== "/login" && window.location.pathname !== "/register" && window.location.pathname !== "/forgot-password" && window.location.pathname !== "/send-email-forgot-password" && window.location.pathname !== "/terms" && window.location.pathname !== "/privacy-policy"){
        window.location.href = "/login";
      }
      this.loading = false;
    }
  },
  mounted() {
    document.body.classList.add("bg-body-secondary");
    window.addEventListener("mobile-chat-open-change", this.onMobileChatOpenChange);
    window.addEventListener("billingRestrictionMessageChanged", this.updateBillingRestrictionMessage);
    this.setConfiguration();
    this.coupleTimer = window.setInterval(() => this.refreshCouple(), 30000);
    window.addEventListener('focus', this.refreshCouple);
    window.addEventListener('couple-access-revoked', this.revokeCoupleView);
  },
  unmounted() {
    window.clearInterval(this.coupleTimer);
    window.removeEventListener('focus', this.refreshCouple);
    window.removeEventListener('couple-access-revoked', this.revokeCoupleView);
    window.removeEventListener("mobile-chat-open-change", this.onMobileChatOpenChange);
    window.removeEventListener("billingRestrictionMessageChanged", this.updateBillingRestrictionMessage);
    this.stopFinanceRealtime();
    stopLorenaVoice().catch(() => undefined);
  },
  setup() {
    const route = useRoute();
    const stateStoreInstance = stateStore;
    
    const currentRoute = computed(() => route.path);

    watchEffect(() => {
      if (stateStoreInstance.open) {
        document.body.classList.remove("sidebar-show");
        document.body.classList.add("sidebar-hide");
      } else {
        document.body.classList.remove("sidebar-hide");
        document.body.classList.add("sidebar-show");
      }
    });

    return {
      currentRoute
    };
  },
});
</script>
<style scoped>
.validate-home-page{
  padding: unset !important;
}

.billing-restriction-modal {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 16px;
  position: fixed;
  z-index: 1055;
}

.billing-restriction-modal__backdrop {
  background-color: rgba(17, 24, 39, 0.45);
  inset: 0;
  position: absolute;
}

.billing-restriction-modal__content {
  background-color: #ffffff;
  border-radius: 7px;
  box-shadow: 0 20px 60px rgba(17, 24, 39, 0.18);
  max-width: 430px;
  padding: 28px;
  position: relative;
  text-align: center;
  width: 100%;
  z-index: 1;
}

.billing-restriction-modal__close {
  align-items: center;
  background-color: transparent;
  border: 0;
  color: #8f9bb3;
  display: flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 14px;
  top: 14px;
  width: 32px;
}

.billing-restriction-modal__icon {
  align-items: center;
  background-color: rgba(255, 193, 7, 0.16);
  border-radius: 50%;
  color: #8a6400;
  display: flex;
  font-size: 28px;
  height: 58px;
  justify-content: center;
  margin: 0 auto 18px;
  width: 58px;
}

.billing-restriction-modal__title {
  color: #111827;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 10px;
}

.billing-restriction-modal__message {
  color: #656f7d;
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 22px;
}

/* When on the Attendants Day page, remove the main-content padding on mobile so the schedule fills the screen. */
@media (max-width: 767px) {
  :deep(.header-burger-menu) {
    display: none !important;
  }

  :deep(.header-area) {
    display: none !important;
  }

  .main-content.with-mobile-bottom-nav {
    padding-top: 24px !important;
    padding-bottom: max(96px, calc(82px + env(safe-area-inset-bottom))) !important;
  }

  .main-content.chat-main.with-mobile-bottom-nav {
    height: 100vh;
    height: 100dvh;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: hidden !important;
    padding-top: 0 !important;
    padding-bottom: max(74px, calc(62px + env(safe-area-inset-bottom))) !important;
  }

  .main-content.chat-main.mobile-chat-open.with-mobile-bottom-nav {
    padding-bottom: 0 !important;
  }

  .main-content.attendants-day-main {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .main-content.attendants-day-main.with-mobile-bottom-nav {
    height: 100vh;
    height: 100dvh;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: hidden !important;
    padding-top: 10px !important;
    padding-bottom: max(74px, calc(62px + env(safe-area-inset-bottom))) !important;
  }
}
</style>
